import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  getRedirectResult,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInAnonymously,
  signInWithCustomToken,
  signInWithPopup,
  signOut,
  updatePassword,
  type UserCredential,
  type User,
} from "firebase/auth";
import type { FirebaseError } from "firebase/app";
import { httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import { functions } from "../firebase";
import { markSignInStage } from "../utils/signInStage";
import {
  adoptCapturedSession,
  beginSessionAdoption,
  captureAnonymousSessionProof,
} from "./anonymousSessionAdoption";

/**
 * The chat ids the anonymous session currently has open in this tab, recorded
 * by ChatsPage in sessionStorage, so the adoption window can hold a deep link
 * to one of them instead of showing "not found" (see beginSessionAdoption).
 */
function rememberedChatIdsForAdoption(): string[] {
  try {
    const raw = window.sessionStorage.getItem("sessionChatIds");
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

type CallableError = {
  code?: string;
  message?: string;
  details?: unknown;
};

type ErrorWithCode = Error & {
  code?: string;
  isExpected?: boolean;
};

const EXPECTED_MAGIC_LINK_ERROR_CODES = new Set([
  "functions/invalid-argument",
  "functions/resource-exhausted",
  "functions/unavailable",
]);

function getCallableErrorCode(err: unknown): string | undefined {
  if (!err || typeof err !== "object") {
    return undefined;
  }
  const error = err as CallableError;
  return typeof error.code === "string" ? error.code : undefined;
}

export function getCallableErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== "object") {
    return fallback;
  }

  const error = err as CallableError;
  let message = typeof error.message === "string" ? error.message : "";
  if (!message && typeof error.details === "string") {
    message = error.details;
  }

  if (!message) {
    return fallback;
  }

  if (message.startsWith("Firebase:")) {
    const trimmed = message.replace(/^Firebase:\s*/i, "").trim();
    const match = trimmed.match(/^(.*?)(?:\s*\(.*\))?$/);
    message = match?.[1]?.trim() || trimmed;
  }

  return message || fallback;
}

export function isExpectedRequestMagicLinkError(err: unknown): boolean {
  if (!err || typeof err !== "object") {
    return false;
  }

  const error = err as ErrorWithCode;
  if (error.isExpected === true) {
    return true;
  }

  return (
    typeof error.code === "string" &&
    EXPECTED_MAGIC_LINK_ERROR_CODES.has(error.code)
  );
}

export async function login(
  email: string,
  password: string
): Promise<UserCredential> {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function loginAnonymously(): Promise<UserCredential> {
  const cred = await signInAnonymously(auth);
  return cred;
}

export function getFirebaseAuthErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }
  const maybeCode = (error as { code?: unknown }).code;
  return typeof maybeCode === "string" ? maybeCode : undefined;
}

function isCredentialConflictError(error: unknown): boolean {
  const code = getFirebaseAuthErrorCode(error);
  return (
    code === "auth/credential-already-in-use" ||
    code === "auth/email-already-in-use" ||
    code === "auth/account-exists-with-different-credential"
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

function logGoogleAuthOutcome(
  outcome: "success" | "failure",
  context: {
    stage: "linkWithPopup" | "signInWithPopup" | "signInWithCredential" | "final";
    code?: string;
    message?: string;
  }
): void {
  const payload = {
    outcome,
    stage: context.stage,
    code: context.code ?? null,
    message: context.message ?? null,
    currentUid: auth.currentUser?.uid ?? null,
    isAnonymous: auth.currentUser?.isAnonymous ?? null,
  };

  if (outcome === "success") {
    console.info("[auth/google] result", payload);
    return;
  }

  console.error("[auth/google] result", payload);
}

async function waitForNonAnonymousUser(timeoutMs = 1500): Promise<boolean> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      return true;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 150));
  }
  return !!auth.currentUser && !auth.currentUser.isAnonymous;
}

let pendingRedirectResolution: Promise<void> | null = null;

export interface GoogleSignInResult {
  /**
   * True only when this sign-in provably registered a new account: a clean
   * anonymous-account link, or a popup sign-in Firebase reports as a new user.
   * The ambiguous recovery paths below (popup error but the session upgraded
   * anyway) report false — a missed SignUp conversion is cheaper than a false
   * one, same trade-off as CompleteRegistration's fail-closed guard.
   */
  isNewUser: boolean;
}

export async function loginWithGoogle(): Promise<GoogleSignInResult> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const currentUser = auth.currentUser;
  const shouldLinkAnonymous = !!currentUser?.isAnonymous;

  // NOTE: nothing may be awaited between the caller's click and
  // `linkWithPopup` below. `getIdToken()` can hit the network, and an await
  // there costs the user-gesture context that browsers require to open a popup
  // — the session proof is therefore captured inside the conflict branch, not
  // here. That is also the only place it is needed: the link failing means the
  // anonymous user is still current, and the success path keeps its uid.
  try {
    if (shouldLinkAnonymous && currentUser) {
      try {
        await linkWithPopup(currentUser, provider);
        // Linking upgrades an anonymous session into a real account — that is
        // the moment this visitor registers.
        logGoogleAuthOutcome("success", { stage: "linkWithPopup" });
        return { isNewUser: true };
      } catch (error) {
        const code = getFirebaseAuthErrorCode(error);

        // Some browsers/extensions can produce popup-related errors after the
        // auth session is already upgraded. Treat that as a successful popup flow.
        if (auth.currentUser && !auth.currentUser.isAnonymous) {
          logGoogleAuthOutcome("success", { stage: "linkWithPopup" });
          return { isNewUser: false };
        }

        // Some browsers update auth state with a slight delay after popup closes.
        // Wait briefly before deciding this truly failed.
        if (await waitForNonAnonymousUser()) {
          logGoogleAuthOutcome("success", { stage: "linkWithPopup" });
          return { isNewUser: false };
        }

        if (!isCredentialConflictError(error)) {
          logGoogleAuthOutcome("failure", {
            stage: "linkWithPopup",
            code,
            message: getErrorMessage(error),
          });
          throw error;
        }
        // Both branches below land on a DIFFERENT uid than the anonymous one,
        // stranding this visitor's chats and quiz data — so each is followed by
        // an adoption. Sign-in has already succeeded at that point; the
        // migration is best-effort and never rethrows.
        //
        // The link failed, so the anonymous user is still signed in and its ID
        // token can still be minted. Capturing here rather than before the
        // popup keeps the gesture context intact for `linkWithPopup`.
        const sessionProof = await captureAnonymousSessionProof();

        const recoveredCredential = GoogleAuthProvider.credentialFromError(
          error as FirebaseError
        );
        if (recoveredCredential) {
          // Conflict recovery: the Google account already belongs to an
          // existing user, so this is a sign-in, never a registration.
          await signInWithCredential(auth, recoveredCredential);
          beginSessionAdoption(sessionProof, rememberedChatIdsForAdoption());
          await adoptCapturedSession(sessionProof, { reason: "google" });
          logGoogleAuthOutcome("success", { stage: "signInWithCredential" });
          return { isNewUser: false };
        }

        const popupCredential = await signInWithPopup(auth, provider);
        beginSessionAdoption(sessionProof, rememberedChatIdsForAdoption());
        await adoptCapturedSession(sessionProof, { reason: "google" });
        logGoogleAuthOutcome("success", { stage: "signInWithPopup" });
        return {
          isNewUser: getAdditionalUserInfo(popupCredential)?.isNewUser ?? false,
        };
      }
    }

    const popupCredential = await signInWithPopup(auth, provider);
    logGoogleAuthOutcome("success", { stage: "signInWithPopup" });
    return {
      isNewUser: getAdditionalUserInfo(popupCredential)?.isNewUser ?? false,
    };
  } catch (error) {
    // Same safety net for the outer popup/sign-in branch.
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      logGoogleAuthOutcome("success", { stage: "final" });
      return { isNewUser: false };
    }

    if (await waitForNonAnonymousUser()) {
      logGoogleAuthOutcome("success", { stage: "final" });
      return { isNewUser: false };
    }

    const code = getFirebaseAuthErrorCode(error);
    logGoogleAuthOutcome("failure", {
      stage: "final",
      code,
      message: getErrorMessage(error),
    });

    throw error;
  }
}

export async function resolvePendingAuthRedirectResult(): Promise<void> {
  if (!pendingRedirectResolution) {
    pendingRedirectResolution = (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          console.log(
            "Redirect auth resolved:",
            result.user.uid,
            result.user.isAnonymous
          );
        } else {
          console.log("Redirect auth resolved: no pending redirect result");
        }
      } catch (error) {
        const code = getFirebaseAuthErrorCode(error);
        if (code !== "auth/no-auth-event") {
          console.warn("Failed to resolve auth redirect result:", error);
        }
      }
    })();
  }
  await pendingRedirectResolution;
}

export async function signup(
  email: string,
  password: string
): Promise<UserCredential> {
  return createUserWithEmailAndPassword(auth, email, password);
}

/**
 * Sign in with email + password.
 */
export async function loginWithPassword(
  email: string,
  password: string
): Promise<UserCredential> {
  // A password login REPLACES an anonymous session rather than upgrading it, so
  // capture proof first and migrate the visitor's chats afterwards. Best-effort:
  // the sign-in has already succeeded by then.
  const sessionProof = await captureAnonymousSessionProof();
  const credential = await signInWithEmailAndPassword(
    auth,
    email.trim(),
    password
  );
  beginSessionAdoption(sessionProof, rememberedChatIdsForAdoption());
  await adoptCapturedSession(sessionProof, { reason: "password" });
  return credential;
}

/**
 * Create an email/password account.
 *
 * When the current session is anonymous, the password credential is LINKED
 * to the anonymous user so its uid — and everything keyed on it (chats,
 * wallet, quiz results) — survives, mirroring the Google `linkWithPopup`
 * path. Otherwise a fresh account is created.
 */
export async function signupWithPassword(
  email: string,
  password: string
): Promise<UserCredential> {
  const trimmedEmail = email.trim();
  const currentUser = auth.currentUser;
  if (currentUser?.isAnonymous) {
    const credential = EmailAuthProvider.credential(trimmedEmail, password);
    return linkWithCredential(currentUser, credential);
  }
  return createUserWithEmailAndPassword(auth, trimmedEmail, password);
}

/**
 * Whether the given user can sign in with a password.
 * Magic-link accounts are admin-created and have no password provider.
 */
export function hasPasswordProvider(user: User | null): boolean {
  return !!user?.providerData.some(
    (provider) => provider.providerId === "password"
  );
}

/**
 * Set (or replace) the password on the currently signed-in account.
 *
 * Magic-link accounts carry no password provider, so the first password is
 * added via linkWithCredential; afterwards updatePassword replaces it. Both
 * may require a recent sign-in — callers should catch
 * `auth/requires-recent-login` and route through the magic link.
 */
export async function setAccountPassword(newPassword: string): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.isAnonymous) {
    throw new Error("No authenticated user");
  }
  if (hasPasswordProvider(currentUser)) {
    await updatePassword(currentUser, newPassword);
    return;
  }
  if (!currentUser.email) {
    throw new Error("Account has no email address");
  }
  const credential = EmailAuthProvider.credential(
    currentUser.email,
    newPassword
  );
  await linkWithCredential(currentUser, credential);
}

/**
 * Request a 6-digit email verification code (custom Mailgun email).
 */
export async function requestEmailVerificationCode(
  language?: string
): Promise<void> {
  const requestFn = httpsCallable<
    { language?: string },
    { success: boolean; expiresInMinutes: number }
  >(functions, "requestEmailVerification");
  await requestFn({ language });
}

/**
 * Confirm the 6-digit email verification code, then refresh the local user
 * so `emailVerified` is reflected in the client session.
 */
export async function confirmEmailVerificationCode(
  code: string
): Promise<void> {
  const verifyFn = httpsCallable<{ code: string }, { success: boolean }>(
    functions,
    "verifyEmailCode"
  );
  await verifyFn({ code });
  if (auth.currentUser) {
    await auth.currentUser.reload().catch(() => undefined);
    await auth.currentUser.getIdToken(true).catch(() => undefined);
  }
}

export async function logout(): Promise<void> {
  await signOut(auth);
  await signInAnonymously(auth);
}

type DeleteOwnAccountRequest = {
  confirmationText: string;
  language?: string;
};

type DeleteOwnAccountResponse = {
  success: boolean;
  uid: string;
  summary: {
    users: number;
    customers: number;
    wallets: number;
    walletTransactions: number;
    chats: number;
    messages: number;
    subscriptions: number;
    invoices: number;
    payments: number;
    usersOnboardings: number;
    usersOnboardingAttempts: number;
    emailVerifications: number;
    authMagicLinks: number;
    asyncMessageQueue: number;
    authUsers: number;
  };
};

export async function deleteAccount(
  request: DeleteOwnAccountRequest
): Promise<DeleteOwnAccountResponse> {
  if (!auth.currentUser) {
    throw new Error("No authenticated user");
  }
  const deleteOwnAccountFn = httpsCallable<
    DeleteOwnAccountRequest,
    DeleteOwnAccountResponse
  >(functions, "deleteOwnAccount");
  const result = await deleteOwnAccountFn(request);
  return result.data;
}

export function onAuthStateChange(
  callback: (isAuthenticated: boolean) => void
): () => void {
  return onAuthStateChanged(auth, (user) => {
    callback(isEmailLinkAuthenticatedUser(user));
  });
}

export function getCurrentUserId(): string | null {
  return auth.currentUser?.uid ?? null;
}

export function getCurrentUser(): User | null {
  return auth.currentUser;
}

export function isEmailLinkAuthenticatedUser(user: User | null): boolean {
  return !!user && !user.isAnonymous;
}

/**
 * Request a Mailgun magic link for sign-in.
 *
 * Sends proof of the current anonymous session so the server can carry this
 * visitor's chats and quiz data onto whatever account the link resolves to.
 * This is the ONLY place that information can come from: the link is often
 * opened in a different browser, where the original uid is unknowable.
 *
 * `uid` is still sent alongside for one release so a server that predates
 * `sourceIdToken` keeps working; it grants only the narrow account upgrade,
 * never a data migration.
 */
export async function requestMagicLink(
  email: string,
  language?: string,
  returnTo?: string
): Promise<void> {
  const proof = await captureAnonymousSessionProof();
  const requestFn = httpsCallable<
    {
      email: string;
      language?: string;
      returnTo?: string;
      uid?: string;
      sourceIdToken?: string;
    },
    { success: boolean }
  >(functions, "requestMagicLink");
  try {
    await requestFn({
      email,
      language,
      returnTo,
      ...(proof && { uid: proof.uid, sourceIdToken: proof.idToken }),
    });
  } catch (error) {
    const wrappedError: ErrorWithCode = new Error(
      getCallableErrorMessage(
        error,
        "Failed to send sign-in link. Please try again."
      )
    );
    const code = getCallableErrorCode(error);
    wrappedError.code = code;
    wrappedError.isExpected =
      typeof code === "string" && EXPECTED_MAGIC_LINK_ERROR_CODES.has(code);
    throw wrappedError;
  }
}

/**
 * Check if the current URL is an email sign-in link
 */
export function isEmailSignInLink(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has("token");
}

export interface EmailSignInResult {
  userCredential: UserCredential;
  /**
   * True when the magic link registered a new account (reported by the
   * verifyMagicLink function, which is the only party that knows whether the
   * account existed before). Missing on older deployed function versions —
   * treated as false so no SignUp conversion fires on a stale backend.
   */
  isNewUser: boolean;
}

/**
 * Complete sign-in with email link
 * @param email - The email address (from localStorage or user prompt)
 * @param token - The magic link token (defaults to the current URL's `token` param)
 */
export async function completeEmailSignIn(
  email: string,
  token?: string
): Promise<EmailSignInResult> {
  const magicToken =
    token ??
    new URLSearchParams(window.location.search).get("token") ??
    "";
  if (!magicToken) {
    throw new Error("Invalid sign-in link");
  }

  const verifyFn = httpsCallable<
    { token: string; email?: string },
    { customToken: string; isNewUser?: boolean }
  >(functions, "verifyMagicLink");
  const trimmedEmail = email?.trim() || "";
  let response;
  try {
    response = await verifyFn({
      token: magicToken,
      ...(trimmedEmail && { email: trimmedEmail }),
    });
  } catch (error) {
    throw markSignInStage(error, "verify_magic_link");
  }

  let userCredential;
  try {
    userCredential = await signInWithCustomToken(
      auth,
      response.data.customToken
    );
  } catch (error) {
    throw markSignInStage(error, "sign_in_with_custom_token");
  }
  return {
    userCredential,
    isNewUser: response.data.isNewUser === true,
  };
}

/**
 * Sign in using a Firebase custom token (payment return flow).
 */
export async function signInWithReturnToken(
  token: string
): Promise<UserCredential> {
  if (!token) {
    throw new Error("Missing return auth token");
  }
  return signInWithCustomToken(auth, token);
}

/**
 * Request a custom token for payment return using payment reference.
 */
export async function requestReturnAuthToken(
  reference: string
): Promise<string> {
  const requestFn = httpsCallable<
    { reference: string },
    { customToken: string }
  >(functions, "createReturnAuthToken");
  const response = await requestFn({ reference });
  return response.data.customToken;
}

